/**
 * useUserNames — tiny hook that resolves staff emails into display names.
 *
 * Fetches `/api/hospital/users` once per page, builds an in-memory map and
 * returns a `nameFor(emailOrName, fallback)` helper. If the input already
 * looks like a human name (no "@") it is returned as-is. Use this everywhere
 * you'd otherwise display `created_by`, `received_by`, `refunded_by`, etc.
 *
 * The result is shared across the whole React tree via a module-level cache
 * so multiple components don't re-fetch.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const cache = { map: null, loading: null };

async function loadDirectory() {
  if (cache.map) return cache.map;
  if (cache.loading) return cache.loading;
  cache.loading = (async () => {
    try {
      const { data } = await api.get("/hospital/staff-directory");
      const m = new Map();
      for (const u of data.items || []) {
        if (u.email) m.set(u.email.toLowerCase(), u.name || u.email);
        if (u.id) m.set(u.id, u.name || u.email || u.id);
      }
      cache.map = m;
      return m;
    } catch {
      cache.map = new Map();
      return cache.map;
    } finally {
      cache.loading = null;
    }
  })();
  return cache.loading;
}

export function nameFor(identifier, map, fallback = "—") {
  const v = (identifier || "").trim();
  if (!v) return fallback;
  // Already a human name (no "@") and not a uuid-like id
  if (!v.includes("@") && !/^[0-9a-f-]{20,}$/i.test(v)) return v;
  if (map && map.size) {
    const hit = map.get(v.toLowerCase()) || map.get(v);
    if (hit) return hit;
  }
  // Last-resort: prettify a username portion of the email
  if (v.includes("@")) {
    const local = v.split("@")[0].replace(/[._-]+/g, " ").trim();
    return local.charAt(0).toUpperCase() + local.slice(1);
  }
  return v;
}

export function useUserNames() {
  const [map, setMap] = useState(cache.map || new Map());
  useEffect(() => {
    let alive = true;
    loadDirectory().then((m) => { if (alive) setMap(m); });
    return () => { alive = false; };
  }, []);
  // Returns a stable function — cheap to depend on
  return (identifier, fallback = "—") => nameFor(identifier, map, fallback);
}
